import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { PageHeader, EmptyState, Modal, inputCls, btnPrimary } from "../../components/bits";
import { Search, Gem, Pencil } from "lucide-react";

const OPP_STATUS = { planned: "مخطط لها", open: "مفتوحة", done: "منفذة", cancelled: "ملغاة" };
const STATUS_CHIP = {
  open: "bg-turq-600 text-white",
  cancelled: "bg-coral-100 text-coral-900 dark:bg-surface2 dark:text-coral-300",
  planned: "bg-turq-100 text-turq-900 dark:bg-surface2 dark:text-turq-300",
};
const statusChipCls = (status) => STATUS_CHIP[status] || "bg-beige-900/40 text-mutext";

export default function AdminOpportunities() {
  const [opps, setOpps] = useState(null);
  const [values, setValues] = useState([]);
  const [q, setQ] = useState("");
  const [value, setValue] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ main_value: "", supporting_values: [], expected_impact: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback((params = "") => api.get(`/admin/opportunities${params}`).then((r) => setOpps(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/public/values").then((r) => setValues(r.data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (value) params.set("value", value);
      load(`?${params}`);
    }, 300);
    return () => clearTimeout(t);
  }, [q, value, load]);

  const openEdit = (o) => {
    setForm({
      main_value: o.main_value || "",
      supporting_values: o.supporting_values || [],
      expected_impact: o.expected_impact || "",
    });
    setModal(o);
  };

  const toggleSupport = (name) => {
    const cur = form.supporting_values || [];
    setForm({ ...form, supporting_values: cur.includes(name) ? cur.filter((v) => v !== name) : [...cur, name] });
  };

  const supportingOptions = useMemo(() => values.filter((v) => v.name !== form.main_value), [values, form.main_value]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/opportunities/${modal.id}`, form);
      toast.success("تم تحديث قيم الفرصة مباشرة");
      setModal(null);
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (value) params.set("value", value);
      load(`?${params}`);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-opportunities-page">
      <PageHeader title="سجل الفرص التطوعية" sub="جميع فرص مدارس الشرقية — بحث بالاسم أو المدرسة، وتعديل ربط القيم مباشرة" />

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute right-3 top-3 text-mutext" />
          <input data-testid="opp-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث باسم الفرصة أو المدرسة…" className={`${inputCls} pr-9`} />
        </div>
        <select data-testid="opp-value-filter" value={value} onChange={(e) => setValue(e.target.value)} className={`${inputCls} !w-52`}>
          <option value="">كل القيم</option>
          {values.map((v) => <option key={v.key} value={v.name}>{v.name}</option>)}
        </select>
      </div>

      {!opps && <div className="text-center text-mutext py-16 font-head">جارٍ التحميل …</div>}
      {opps && opps.length === 0 && <EmptyState testid="admin-opps-empty" text="لا توجد فرص مطابقة للبحث" />}
      {opps && opps.length > 0 && (
        <div className="bg-surface rounded-2xl shadow-md border border-brd/50 overflow-auto">
          <table className="w-full text-sm min-w-[920px]" data-testid="admin-opps-table">
            <thead>
              <tr className="bg-turq-900 text-white">
                <th className="text-right px-4 py-3 font-head text-xs">الفرصة</th>
                <th className="text-right px-4 py-3 font-head text-xs">المدرسة</th>
                <th className="text-right px-4 py-3 font-head text-xs">القيمة الأساسية</th>
                <th className="text-right px-4 py-3 font-head text-xs">الآثار المتوقعة</th>
                <th className="text-right px-4 py-3 font-head text-xs">المشاركة</th>
                <th className="text-right px-4 py-3 font-head text-xs">الحالة</th>
                <th className="px-4 py-3 font-head text-xs">تعديل</th>
              </tr>
            </thead>
            <tbody>
              {opps.map((o) => (
                <tr key={o.id} data-testid={`admin-opp-row-${o.id.slice(0, 8)}`} className="border-b border-brd/50 last:border-0 hover:bg-bg transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-bold">{o.name}</div>
                    <div className="text-[10px] text-mutext">{o.start_date} · {o.location || o.category}</div>
                  </td>
                  <td className="px-4 py-3 text-[12px]">{o.school_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-[10.5px] font-bold bg-coral-100 text-coral-900 dark:bg-surface2 dark:text-coral-300 rounded-full px-2.5 py-1">
                      <Gem className="w-3 h-3" /> {o.main_value}
                    </span>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {(o.supporting_values || []).map((v) => (
                        <span key={v} className="text-[9.5px] font-bold bg-turq-100 text-turq-900 dark:bg-surface2 dark:text-turq-300 rounded-full px-2 py-0.5">{v}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-mutext max-w-[200px]">{o.expected_impact || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] font-bold bg-beige-600 dark:bg-surface2 rounded-full px-2.5 py-1">
                      {o.participants} مشارك · {o.hours} ساعة
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10.5px] font-bold rounded-full px-3 py-1 ${statusChipCls(o.status)}`}>
                      {OPP_STATUS[o.status] || "منفذة"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button data-testid={`admin-opp-edit-${o.id.slice(0, 8)}`} onClick={() => openEdit(o)} title="تعديل ربط القيم"
                      className="w-8 h-8 rounded-lg bg-beige-600 dark:bg-surface2 flex items-center justify-center hover:scale-105 transition-transform">
                      <Pencil className="w-3.5 h-3.5 text-turq-900 dark:text-turq-300" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)} title={`ربط قيم الفرصة — «${modal?.name || ""}»`} testid="admin-opp-values-modal">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">القيمة الرئيسية *</label>
            <select data-testid="admin-opp-main-value-select" value={form.main_value}
              onChange={(e) => setForm({ ...form, main_value: e.target.value })} className={inputCls}>
              <option value="">— اختر القيمة —</option>
              {values.map((v) => <option key={v.key} value={v.name}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">القيم المساندة</label>
            <div className="flex flex-wrap gap-1.5" data-testid="admin-opp-supporting-values">
              {supportingOptions.map((v) => (
                <button type="button" key={v.key} data-testid={`admin-opp-support-${v.key}`} onClick={() => toggleSupport(v.name)}
                  className={`text-[11px] font-bold rounded-full px-3 py-1.5 border transition-colors ${
                    (form.supporting_values || []).includes(v.name)
                      ? "bg-turq-600 text-white border-turq-600"
                      : "bg-surface text-mutext border-brd hover:border-turq-600"}`}>
                  {v.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">الأثر المتوقع</label>
            <input data-testid="admin-opp-impact-input" value={form.expected_impact}
              onChange={(e) => setForm({ ...form, expected_impact: e.target.value })} className={inputCls}
              placeholder="مثال: الالتزام بالنظام والوقت" />
          </div>
          <button data-testid="admin-opp-values-save" onClick={save} disabled={busy} className={`${btnPrimary} w-full justify-center`}>
            {busy ? "جارٍ الحفظ …" : "حفظ الربط"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
